<template>
  <v-row class>
    <v-col cols="12" class="pb-0 d-flex align-center justify-space-between">
      <!--Search-->
      <v-autocomplete dense hide-details clearable return-object filled rounded :background-color="$vuetify.theme.dark ? '' : 'white'"
        :placeholder="$t('general.search')" prepend-inner-icon="search" class="body-1 me-5" style="max-width: 400px;" :key="searchbarKey"
        @change="onSearchChange()" v-model="searchedLeague" :items="leagues" item-text="label" item-value="id">
        <template v-slot:selection="{item, index}">
          <v-chip small color="lightSecondary lighten-2" class="body-1 mb-1 black--text">
            <span class="font-weight-medium">{{ item.label }}</span>
          </v-chip>
        </template>
        <template v-slot:item="{item}">
          <v-card-text class="pa-0 primary--text font-weight-medium">
            <span>{{ item.label }}</span>
          </v-card-text>
        </template>
      </v-autocomplete>
      <!--League in progress-->
      <v-alert dense class="ma-0 py-1 align-self-start primary--text">
        <v-icon x-small color="green" class="mr-1">fa-square</v-icon>
        <span class="body-2">= {{ $t("general.activeLeague") }}</span>
      </v-alert>
    </v-col>
    <v-col>
      <v-card elevation="0" outlined>
        <v-card-title class="pt-1 pb-3" :class="$vuetify.theme.dark ? '' : 'primary'">
          <!--Areas-->
          <v-tabs v-model="areaSelected" :background-color="$vuetify.theme.dark ? '' : 'primary'" slider-color="white" dark>
            <v-tab :value="0" v-show="top5Leagues.length">
              <span class="monospace-font body-1" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">TOP 5</span>
            </v-tab>
            <v-tab v-for="(area, i) in availableLeagueAreas" :key="area.id" :value="area.order">
              <span class="monospace-font" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">
                {{ area.name }}
              </span>
            </v-tab>
          </v-tabs>
        </v-card-title>
        <v-divider color="white"></v-divider>
        <v-tabs-items v-model="areaSelected">
          <!-- Top 5 -->
          <v-tab-item :value="0" class="px-5 py-2" v-show="top5Leagues.length">
            <!--Not showing seasons-->
            <v-btn x-small outlined color="primary" class="ma-1 py-4 px-2" elevation="0" 
              :class="{'grey lighten-1': showActiveStyle(league) && !$vuetify.theme.dark, 'lightSecondary': showActiveStyle(league) && $vuetify.theme.dark , 'black lighten-3': $vuetify.theme.dark && !showActiveStyle(league), 'grey lighten-4': !$vuetify.theme.dark && !showActiveStyle(league)}"
              v-if="!showSeasons"
              :value="league.id" :key="league.id" 
              v-for="league in top5Leagues" 
              @click="onLeagueClick(league)"
            >
              <img contain class="me-2" height="16"
                :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + league.id + '.png'"
                @error="onImageLoadFailure($event)"
              />
              <span v-if="league.label" class="font-weight-bold">{{ league.label }}</span>
              <v-badge tile dot color="green" v-if="league.seasonActive" offset-y="-8" offset-x="-0.4"></v-badge>
            </v-btn>
            <!--Show seasons menu-->
            <v-menu v-if="showSeasons" bottom rounded offset-y v-for="league in top5Leagues" :key="league.id">
              <template v-slot:activator="{ on, attrs }">
                <v-btn x-small outlined color="primary" class="ma-1 py-4 px-2" elevation="0"
                  :class="{'grey lighten-1': showActiveStyle(league) && !$vuetify.theme.dark, 'lightSecondary': showActiveStyle(league) && $vuetify.theme.dark , 'black lighten-3': $vuetify.theme.dark && !showActiveStyle(league), 'grey lighten-4': !$vuetify.theme.dark && !showActiveStyle(league)}"
                  :value="league.id" :key="league.id" 
                  v-bind="attrs" v-on="on"
                >
                  <img contain class="me-2" height="16"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + league.id + '.png'"
                    @error="onImageLoadFailure($event)"
                  />
                  <span v-if="league.label" class="font-weight-bold">{{league.label}}</span>
                  <v-badge tile dot color="green" v-if="league.seasonActive" offset-y="-8" offset-x="-0.4"></v-badge>
                </v-btn>
              </template>
              <v-list rounded dense>
                <v-list-item v-for="(year,index) in league.years" :key="index" @click="onYearClick({ year, league })">
                  <v-list-item-content>
                    <v-list-item-title>{{ year }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-tab-item>
          <v-tab-item v-for="(area, i) in availableLeagueAreas" :key="area.id" :value="area.order">
            <DivisionTabs :key="divisionTabsKey"
              :groupedDivisions="groupedLeagues[area.areaName]"
              :availableDivisions="divisions"
              @leagueClicked="onLeagueClick($event)"
              :selectedLeague="selectedLeagueObject"
              :searchedLeague="searchedLeague"
              :showSeasons="showSeasons"
              @yearClicked="onYearClick($event)"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>

import LeagueService from '../../../services/league.service';
import DriblabCountryFlag from '../../base/DriblabCountryFlag';
import DivisionTabs from './DivisionTabs.vue';
import GeneralFilter from '../../base/GeneralFilter.vue';

export default {
  name: "LeagueSelector",
  props: {
    showSeasons: {
      //Show season years when clicking on a league
      type: Boolean,
      default: false
    },
    isHandlingLeagueSelection: {
      //Inner track of selected league
      type: Boolean,
      default: false
    }
  },
  components: {
    DriblabCountryFlag,
    DivisionTabs,
    GeneralFilter
  },
  title () {
    if(this.selectedLeagueObject){
      return this.selectedLeagueObject.label;
    }else {
      return this.$t("app.leagues");
    }
  },
  data() {
    return {
      loading: false,
      leagues: [],
      selectedLeague: null,
      selectedLeagueObject: null,
      overrideWindowTitle: true,
      areaSelected: null,
      leagueAreas: [
        { areaName: "Europe", order: 1, name: "UEFA", areas: [0, 1, 2] },
        { areaName: "South America", order: 2, name: "CONMEBOL", areas: [3, 4] },
        { areaName: "North America", order: 3, name: "CONCAF", areas: [5] },
        { areaName: "Asia", order: 5, name: "AFC", areas: [6] },
        { areaName: "Africa", order: 4, name: "CAF", areas: [7] },
        { areaName: "Youth", order: 7, name: "Youth", areas: [8, 9] },
        { areaName: "National Team", order: 6, name: "National Team", areas: [10] },
        { areaName: "Other Competitions", order: 9, name: "Other Competitions", areas: [11] },
        { areaName: "Women", order: 8, name: "Women", areas: [12] },
        { areaName: "Unknown", order: 10, name: "Unknown", areas: [99] },
      ],
      groupedLeagues: null,
      searchedLeague: null,
      divisionTabsKey: 0,
      searchbarKey: 10,
      divisions: [0, 1, 2, 3, 4, 5]
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    availableLeagueAreas() {
      if (this.groupedLeagues) {
        const areas = Object.keys(this.groupedLeagues);
        return areas
          .map(areaName => {
            const leagueArea = this.leagueAreas.find(area => area.areaName === areaName) || {}; // Avoid undefined errors
            return {
              areaName: areaName,
              order: leagueArea.order ? leagueArea.order : 99,
              name: leagueArea.name || areaName
            };
          })
          .sort((a, b) => a.order - b.order);
      };
      return [];
    },
    top5Leagues() {
      //Premier League, La Liga, Bundesliga, France Ligue 1, Serie A
      if (this.leagues.length) {
        return _.chain(this.leagues)
        .filter(l => [1923, 1, 4129, 7662, 7].includes(l.id))
        .orderBy(["isHired", "ranking", "label"], ["desc", "asc", "asc"])
        .value();
      } else return [];
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadLeagueData();

    if (this.showSeasons) {
      this.selectDefaultSeasons();
    } else if (this.isHandlingLeagueSelection) {
      this.selectDefaultLeague();
      this.onLeagueClick(this.selectedLeagueObject);
    }; 

    this.loading = false;
  },
  methods: {
    async loadLeagueData() {
      let leaguesResponse = await LeagueService.getAllLeagues();
      this.leagues = _.orderBy(leaguesResponse.data, [(l) => !l.elo ? 0 : l.elo, 'division', 'area'], ['desc', 'asc', 'asc']);
      
      this.groupedLeagues = _.mapValues(
        // Group by ‘areaName’ after mapping ‘area’ to ‘areaName’ using the new structure of leagueAreas
        _.groupBy(leaguesResponse.data.map(league => {
          return {
            ...league,
            areaName: (this.leagueAreas.find(areaObj => areaObj.areas.includes(league.area)) || { areaName: "Unknown" }).areaName,
            division: this.getLeagueCategory(league) === 'Continental' ? 0 : league.division
          };
        }), 'areaName'),
        // Sort each group by ‘area’ and then group by ‘division’.
        areaGroup => _.mapValues(
          _.groupBy(_.orderBy(areaGroup, ['area'], ['asc']), 'division'),
          // Sort each division by elo
          divisionGroup => _.orderBy(divisionGroup, [(item) => !item.elo ? 0 : item.elo], ['desc'])
        )
      );
    },
    onLeagueClick(league) {
      if (this.isHandlingLeagueSelection) {
        this.selectedLeague = league.id;
        this.selectedLeagueObject = league;
      };
      this.$emit('leagueClicked', league);
    },
    onYearClick(event) {
      this.onSearchClear();
      this.$emit('yearClicked', event);
    },
    onImageLoadFailure(event) {
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
    onSearchChange() {
      if (this.searchedLeague) {
        if (this.isHandlingLeagueSelection) {
          this.selectedLeagueObject = this.searchedLeague;
          this.selectedLeague = this.searchedLeague.id;
        };
        this.areaSelected = this.leagueAreas.find(areaObj => areaObj.areas.includes(this.searchedLeague.area)).order;
        this.divisionTabsKey++;
        this.onLeagueClick(this.searchedLeague);
      };
    },
    getLeagueCategory(league) {
      if (league.leagueTypeId === 2 || league.leagueTypeId === 4) {
        return league.leagueType;
      } else if (league.leagueTypeId === 1) {
        return league.division;
      } else return 'Other';
    },
    onSearchClear() {
      this.searchedLeague = null;
      this.searchbarKey++;
    },
    selectDefaultSeasons() {
      const getLatestSeason = (league) => ({
        year: _.max(league.years),
        league
      });

      const getMatchingLeagues = (condition) => 
        _.chain(this.leagues)
          .filter(condition)
          .orderBy(["ranking", "label"], ["asc", "asc"])
          .map(getLatestSeason)
          .filter(s => s.league.isRegular)
          .value();

      // user.league or user.team.leagueId
      let defaultSeasons = getMatchingLeagues(l =>
        (this.currentUser.league && l.id === this.currentUser.league.id) ||
        (this.currentUser.team && l.id === this.currentUser.team.leagueId)
      );

      // Country and division <= 1
      if (_.isEmpty(defaultSeasons)) {
        const countryCode = _.get(this.currentUser, "team.leagueCountryCode", _.get(this.currentUser, "league.countryCode"));
        defaultSeasons = getMatchingLeagues(l =>
          l.countryCode === countryCode && l.division <= 1
        );
      }

      // Last two ordered by ranking and label
      if (_.isEmpty(defaultSeasons)) {
        defaultSeasons = _.chain(this.leagues)
          .orderBy(["ranking", "label"], ["asc", "asc"])
          .take(2)
          .map(getLatestSeason)
          .filter(s => s.league.isRegular)
          .value();
      }

      // Apply selection
      defaultSeasons.forEach(ds => {
        this.onYearClick({ year: ds.year, league: ds.league });
      });
    },
    showActiveStyle(league) {
      return (this.searchedLeague && this.searchedLeague.id === league.id) || (this.selectedLeagueObject && this.selectedLeagueObject.id === league.id);
    },
    selectDefaultLeague() {
      this.selectedLeagueObject = this.currentUser.league ? this.leagues.find(l => l.id === this.currentUser.league.id) : null;
      this.selectedLeague = this.currentUser.league ? this.currentUser.league.id : null;
      if (!this.selectedLeagueObject) {
        this.selectedLeagueObject = _.sample(this.leagues);
        this.selectedLeague = this.selectedLeagueObject.id;
      };
      this.areaSelected = this.top5Leagues.length && this.top5Leagues.some(l => l.id === this.selectedLeague) ? 0 
        : this.leagueAreas.find(areaObj => areaObj.areas.includes(this.selectedLeagueObject.area)).order;
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/views/league/league-selector/LeagueSelector.vue